import { linkFiles, useUpload } from "@/api/files";
import { FileCardRemote } from "@/components/files";
import { useFileCapabilities } from "@/hooks/use-file-capabilities";
import { useWikiPageFiles } from "@/hooks/use-wiki-page-files";
import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { FileUpload, useFileUpload } from "@msuite/ui/file-upload";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import type { Tables } from "@msuite/utils/types";
import React from "react";

/** Props Interface */
interface WikiPageFilesProps {
	/** The page the files hang on, in the shape the `Wiki` rule binds on. */
	page: Pick<Tables<"wikiPages">, "id" | "orgId">;
	/** The files the page's text embeds as pictures; they are shown in the text, not listed here. */
	embeddedFileIds: string[];
}

/**
 * The page's attachments, beneath the document: the files hung on this page, and a dropzone for
 * adding to them. They live outside the page form, like the thread, so adding or removing one is
 * saved at once and never meets the unsaved-changes guard; the editor does not show them.
 *
 * A card offers download and delete, not unlink. A wiki attachment is uploaded on its page and
 * linked nowhere else — there is no gallery to pick it into a second place — so taking the link
 * away would only leave the file behind where nobody sees it.
 */
export function WikiPageFiles({ page, embeddedFileIds }: WikiPageFilesProps) {
	/** Hooks */
	const { t } = useTranslation();
	const capabilities = useFileCapabilities({ subject: "Wiki", resource: page });
	const dropzone = useDisclosure();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({ uploadFn });

	/** Query */
	const { files, refetch } = useWikiPageFiles(page.id);

	/** Functions */
	async function handleOnUpload() {
		const uploadResults = await controller.handleOnFileUpload();
		if (!uploadResults.length) return;
		await linkFiles("wikiPageFiles", { wikiPageId: page.id }, { uploadResults });
		controller.handleOnReset();
		dropzone.onClose();
		await refetch();
	}

	function handleOnCloseDropzone() {
		controller.handleOnReset();
		dropzone.onClose();
	}

	/** Computed */
	// A page's attachments follow the page: whoever may edit it may attach to it and delete what
	// hangs there, and nobody else may do either.
	const canAttach = capabilities.canWrite;
	// A picture embedded in the text is the page's file as well, linked the same way, but it is
	// shown where it sits. Taken out of the text, it appears here, where it can be deleted.
	const attachments = files.filter((file) => !embeddedFileIds.includes(file.id));

	/** Guard */
	if (attachments.length === 0 && !canAttach) return null;

	/** Render */
	return (
		<React.Fragment>
			<Divider className="mt-10 mb-5" />
			<section className="v-stack">
				<div className="flex min-h-8 flex-row items-center justify-between gap-4">
					<p className="label">
						{t("wiki.files")} ({attachments.length})
					</p>
					{canAttach && (
						<Button
							variant="ghost"
							icon={<Icon name="Paperclip" />}
							onClick={dropzone.onToggle}
						>
							{t("wiki.attach_file")}
						</Button>
					)}
				</div>
				{attachments.length > 0 && (
					<div className="flex flex-col gap-2">
						{attachments.map((file) => (
							<FileCardRemote
								key={file.id}
								fileId={file.id}
								canRemove={capabilities.canWrite}
								onInteraction={async () => {
									await refetch();
								}}
							/>
						))}
					</div>
				)}
				{canAttach && dropzone.isOpen && (
					<div className="v-stack">
						<FileUpload
							withLabel={false}
							controller={controller}
							renderFile={() => null}
						/>
						<div className="flex flex-row justify-end gap-2">
							<Button
								variant="ghost"
								onClick={handleOnCloseDropzone}
							>
								{t("general.cancel")}
							</Button>
							<Button
								variant="primary"
								onClick={handleOnUpload}
								isLoading={controller.isUploading}
								isDisabled={!controller.hasFiles}
							>
								{t("file_upload.upload_files")}
							</Button>
						</div>
					</div>
				)}
			</section>
		</React.Fragment>
	);
}
